import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { Button, buttonVariants } from './button';
import { useResponsiveToolbar } from './responsive-toolbar';
import { cn } from '@/lib/utils';
import type { VariantProps } from 'class-variance-authority';

/**
 * Toolbar control sizing (CH-22 / PP-31).
 *
 * `ToolbarButton` and `ToolbarDropdownButton` already forward `size` straight
 * into `buttonVariants`, so they pick up the scale defined there for free:
 * `xs` = 28px compact, `sm` = 32px (their default, unchanged — the main
 * toolbar's 40px band is an `h-8` control plus the strip's `py-1`, pinned by
 * `integratedTitlebar.spec.ts`), `default` = 36px.
 *
 * `ToolbarPlainButton` is not a shadcn button, so it carries its own copy of
 * the scale below. It already rendered at 28px (`py-1` around a 20px line
 * box), so `xs` is its default and nothing moves.
 */
const PLAIN_BUTTON_SIZES = {
  xs: 'py-1',
  sm: 'py-1.5',
  default: 'py-2',
} as const;

type PlainButtonSize = keyof typeof PLAIN_BUTTON_SIZES;

interface ToolbarButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  icon: React.ReactNode;
  label: string;
  /** Whether this button is in the right (collapsible) section */
  collapsible?: boolean;
  asChild?: boolean;
}

/**
 * ToolbarButton renders a button with icon + label.
 * When in a collapsible section and space is limited, it collapses to icon-only.
 */
export const ToolbarButton = React.forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  ({ icon, label, collapsible = false, className, variant, size = 'sm', ...props }, ref) => {
    const { rightCollapseState } = useResponsiveToolbar();
    const isCollapsed = collapsible && rightCollapseState === 'collapsed';

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          'flex items-center gap-1.5',
          isCollapsed && 'px-2',
          className
        )}
        data-toolbar-item
        title={isCollapsed ? label : props.title}
        {...props}
      >
        {icon}
        {!isCollapsed && <span>{label}</span>}
      </Button>
    );
  }
);
ToolbarButton.displayName = 'ToolbarButton';

interface ToolbarDropdownButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  icon: React.ReactNode;
  label: string;
  /** Whether this button is in the right (collapsible) section */
  collapsible?: boolean;
}

/**
 * ToolbarDropdownButton renders a dropdown trigger with icon + label + chevron.
 * When in a collapsible section and space is limited, it collapses to icon + chevron.
 */
export const ToolbarDropdownButton = React.forwardRef<HTMLButtonElement, ToolbarDropdownButtonProps>(
  ({ icon, label, collapsible = false, className, variant = 'outline', size = 'sm', ...props }, ref) => {
    const { rightCollapseState } = useResponsiveToolbar();
    const isCollapsed = collapsible && rightCollapseState === 'collapsed';

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          'flex items-center gap-1.5 px-2',
          className
        )}
        data-toolbar-item
        title={isCollapsed ? label : props.title}
        {...props}
      >
        {icon}
        {!isCollapsed && <span>{label}</span>}
        <ChevronDown className="h-4 w-4" />
      </Button>
    );
  }
);
ToolbarDropdownButton.displayName = 'ToolbarDropdownButton';

interface ToolbarPlainButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode;
  label: string;
  /** Whether this button is in the right (collapsible) section */
  collapsible?: boolean;
  /** Always show only the icon (no label) */
  iconOnly?: boolean;
  /** Row height: `xs` 28px (default, unchanged), `sm` 32px, `default` 36px. */
  size?: PlainButtonSize;
}

/**
 * ToolbarPlainButton is a non-shadcn toolbar button that collapses to icon-only.
 * Useful for matching existing MainToolbar button styles.
 */
export const ToolbarPlainButton = React.forwardRef<HTMLButtonElement, ToolbarPlainButtonProps>(
  ({ icon, label, collapsible = false, iconOnly = false, size = 'xs', className, ...props }, ref) => {
    const { rightCollapseState } = useResponsiveToolbar();
    const isCollapsed = collapsible && rightCollapseState === 'collapsed';
    const showIconOnly = iconOnly || isCollapsed;

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          'flex items-center gap-1.5 px-2 text-sm rounded-sm hover:bg-muted',
          PLAIN_BUTTON_SIZES[size],
          'transition-[color,background-color,scale] active:scale-[0.96]',
          // Extend the hit area vertically to fill the 40px toolbar strip
          // (buttons sit 8px apart horizontally, so only the vertical axis
          // can grow without overlapping a neighbor's hit area).
          "relative before:absolute before:inset-x-0 before:-inset-y-1.5 before:content-['']",
          showIconOnly && 'px-1.5',
          className
        )}
        data-toolbar-item
        title={showIconOnly ? label : props.title}
        {...props}
      >
        {icon}
        {!showIconOnly && <span>{label}</span>}
      </button>
    );
  }
);
ToolbarPlainButton.displayName = 'ToolbarPlainButton';
